import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../App.module.scss"
import styles from "./KubernetesCheatSheet.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../Architecture/shared/Diagram"

const deploymentYamlExample = `apiVersion: apps/v1
kind: Deployment
metadata:
  name: hello-web
spec:
  replicas: 3
  selector:
    matchLabels:
      app: hello-web
  template:
    metadata:
      labels:
        app: hello-web
    spec:
      containers:
        - name: hello-web
          image: hello-web:1.4.0
          ports:
            - containerPort: 8080
          resources:
            requests:
              cpu: 100m
              memory: 128Mi
            limits:
              cpu: 500m
              memory: 256Mi
---
apiVersion: v1
kind: Service
metadata:
  name: hello-web
spec:
  selector:
    app: hello-web
  ports:
    - port: 80
      targetPort: 8080`

export default function KubernetesCheatSheet() {
    return (
        <div className={globalStyles.article}>
            <h1>Kubernetes in a Nutshell</h1>

            <p>Kubernetes is a container orchestrator: given a fleet of machines and a set of containers, it
                decides which machine runs each container, keeps the declared number of copies running,
                replaces the ones that die, rolls out changes gradually, and gives every service a stable
                network identity — all driven by declarative YAML instead of a person running commands by
                hand.</p>

            <h3>The core idea: desired state, reconciled continuously</h3>
            <p>You describe what you want — "3 replicas of this image, exposed on this port" — as an object
                in the cluster's API. Controllers loop forever, comparing that <b>desired state</b> to the
                cluster's <b>actual state</b>, and take action to close the gap. Nothing runs "once": if a
                Pod dies, the same loop notices and starts a replacement without anyone paging.</p>

            <h3>Architecture at a glance</h3>
            <Diagram className={styles.architectureDiagram} viewBox="0 0 1010 495">
                <rect x={180} y={12} width={600} height={204} rx={10} fill="none"
                      stroke="var(--header-background)" strokeDasharray="4 4" opacity={0.4}/>
                <text x={480} y={31} textAnchor="middle" fontSize={12} opacity={0.8}>Control plane</text>

                <rect x={180} y={276} width={600} height={204} rx={10} fill="none"
                      stroke="var(--header-background)" strokeDasharray="4 4" opacity={0.4}/>
                <text x={480} y={295} textAnchor="middle" fontSize={12} opacity={0.8}>Worker node</text>

                <Box x={12} y={53} width={120} height={44} label="kubectl" sublabel="CLI"/>
                <Box x={252} y={53} width={168} height={44} label="API Server" sublabel="REST + auth" accent/>
                <Box x={552} y={53} width={132} height={44} label="etcd" sublabel="key-value store"/>
                <Box x={252} y={144} width={168} height={44} label="Scheduler" sublabel="picks a node"/>
                <Box x={552} y={144} width={204} height={44} label="Controller Mgr" sublabel="reconcile loops"/>

                <Box x={252} y={324} width={168} height={44} label="kubelet" sublabel="runs Pods"/>
                <Box x={528} y={324} width={168} height={44} label="kube-proxy" sublabel="Service routing"/>
                <Box x={324} y={408} width={336} height={44} label="Pods" sublabel="your containers"/>

                <Box x={864} y={324} width={120} height={44} label="Users" sublabel="traffic"/>

                <Arrow x1={132} y1={75} x2={252} y2={75} label="apply -f"/>
                <Arrow x1={420} y1={75} x2={552} y2={75} label="stores state"/>
                <Arrow x1={336} y1={97} x2={336} y2={144} label="new pod"/>
                <Arrow x1={552} y1={144} x2={420} y2={97} label="watch & reconcile" dashed muted/>
                <Arrow x1={336} y1={188} x2={336} y2={324} label="assigns node" dashed/>
                <Arrow x1={336} y1={368} x2={396} y2={408} label="starts containers"/>
                <Arrow x1={864} y1={346} x2={696} y2={346} label="Service traffic"/>
                <Arrow x1={612} y1={368} x2={576} y2={408} label="load-balances" dashed/>
            </Diagram>
            <DiagramCaption>You talk to the API Server, never directly to a node. It persists everything
                in etcd, the Scheduler assigns new Pods to a node, and each node's kubelet actually starts
                the containers and reports back — the Controller Manager watches for drift the whole time.
            </DiagramCaption>

            <h3>Core objects, at a glance</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Object</th>
                        <th>What it is</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>Pod</code></td>
                        <td>The smallest deployable unit — one or more containers that always share a
                            network namespace and are scheduled together
                        </td>
                    </tr>
                    <tr>
                        <td><code>ReplicaSet</code></td>
                        <td>Keeps a fixed number of identical Pods running; you rarely create these
                            directly
                        </td>
                    </tr>
                    <tr>
                        <td><code>Deployment</code></td>
                        <td>Manages a ReplicaSet for you and handles rolling updates and rollbacks — the
                            normal way to run stateless workloads
                        </td>
                    </tr>
                    <tr>
                        <td><code>StatefulSet</code></td>
                        <td>Like a Deployment, but each Pod gets a stable name and its own persistent
                            storage — for databases and anything with identity
                        </td>
                    </tr>
                    <tr>
                        <td><code>DaemonSet</code></td>
                        <td>Runs exactly one copy of a Pod on every (or every matching) node — log
                            collectors, node exporters
                        </td>
                    </tr>
                    <tr>
                        <td><code>Job</code> / <code>CronJob</code></td>
                        <td>Runs a Pod to completion once, or on a schedule, instead of keeping it running
                            forever
                        </td>
                    </tr>
                    <tr>
                        <td><code>Service</code></td>
                        <td>A stable virtual IP and DNS name that load-balances traffic to whichever Pods
                            currently match its label selector
                        </td>
                    </tr>
                    <tr>
                        <td><code>Ingress</code></td>
                        <td>HTTP(S) routing rules (host/path → Service) handled by an ingress controller in
                            front of your Services
                        </td>
                    </tr>
                    <tr>
                        <td><code>ConfigMap</code></td>
                        <td>Non-secret configuration, injected as environment variables or mounted files</td>
                    </tr>
                    <tr>
                        <td><code>Secret</code></td>
                        <td>Same idea as a ConfigMap, for sensitive values — base64-encoded, not encrypted,
                            by default
                        </td>
                    </tr>
                    <tr>
                        <td><code>Namespace</code></td>
                        <td>A logical partition of the cluster — names, quotas and RBAC are scoped per
                            namespace
                        </td>
                    </tr>
                    <tr>
                        <td><code>PV</code> / <code>PVC</code> / <code>StorageClass</code></td>
                        <td>How Pods get durable disk — see <b>Storage</b> below</td>
                    </tr>
                    <tr>
                        <td><code>HorizontalPodAutoscaler</code></td>
                        <td>Adjusts a Deployment's replica count automatically based on observed CPU/memory
                            or custom metrics
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>How objects relate: Deployment &rarr; ReplicaSet &rarr; Pods &rarr; Service</h3>
            <Diagram viewBox="0 0 840 260">
                <Box x={20} y={90} width={150} height={44} label="Deployment" sublabel="desired: 3 replicas"
                     accent/>
                <Box x={250} y={90} width={150} height={44} label="ReplicaSet" sublabel="ensures the count"/>

                <Box x={490} y={20} width={100} height={36} label="Pod" sublabel="app=hello-web"/>
                <Box x={490} y={94} width={100} height={36} label="Pod" sublabel="app=hello-web"/>
                <Box x={490} y={168} width={100} height={36} label="Pod" sublabel="app=hello-web"/>

                <Box x={680} y={94} width={140} height={36} label="Service" sublabel="selector: app=hello-web"/>

                <Arrow x1={170} y1={112} x2={250} y2={112} label="owns"/>
                <Arrow x1={400} y1={112} x2={490} y2={112} label="creates x3"/>
                <Arrow x1={680} y1={112} x2={590} y2={112} label="via selector" dashed/>
            </Diagram>
            <DiagramCaption>You edit the Deployment; it and the ReplicaSet it owns do the rest. The Service
                never talks to the Deployment at all — it matches every Pod whose labels fit its selector
                (all three here, though only the middle one is drawn for clarity), which is also how a
                rolling update swaps traffic over to new Pods gradually.
            </DiagramCaption>

            <h3>Networking &amp; Service types</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Type</th>
                        <th>Reachable from</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>ClusterIP</code> (default)</td>
                        <td>Only inside the cluster — the normal way one service talks to another</td>
                    </tr>
                    <tr>
                        <td><code>NodePort</code></td>
                        <td>Every node's IP on a fixed high port (30000-32767) — mostly a building block for
                            other things
                        </td>
                    </tr>
                    <tr>
                        <td><code>LoadBalancer</code></td>
                        <td>An external load balancer provisioned by the cloud provider, pointing at a
                            NodePort
                        </td>
                    </tr>
                    <tr>
                        <td><code>ExternalName</code></td>
                        <td>Just a DNS CNAME to something outside the cluster — no proxying involved</td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <p>An <code>Ingress</code> sits in front of one or more <code>ClusterIP</code> Services and adds
                host/path-based HTTP routing and TLS termination, so you don't need a separate
                <code> LoadBalancer</code> per service.</p>

            <h3>Storage: PV, PVC &amp; StorageClass</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Layer</th>
                        <th>Role</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>StorageClass</b></td>
                        <td>A template describing how to provision storage on demand (which backend, which
                            disk type)
                        </td>
                    </tr>
                    <tr>
                        <td><b>PersistentVolume (PV)</b></td>
                        <td>An actual piece of storage, either provisioned ahead of time or created on
                            demand from a StorageClass
                        </td>
                    </tr>
                    <tr>
                        <td><b>PersistentVolumeClaim (PVC)</b></td>
                        <td>What a Pod actually asks for ("50Gi, read-write-once") — Kubernetes binds it to
                            a matching PV
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Config &amp; secrets</h3>
            <p>Both <code>ConfigMap</code> and <code>Secret</code> are consumed the same two ways: as
                individual environment variables, or as files mounted into the container. Mounted files
                update on the fly when the object changes (though the app still has to notice);
                environment variables are fixed at container start and never update on their own. Either
                way, the reliable fix when Pods need to pick up a change is{' '}
                <code>kubectl rollout restart deployment/hello-web</code>.</p>

            <h3>A minimal example</h3>
            <p>A Deployment and the Service that fronts it — about as small as a real one gets:</p>
            <SyntaxHighlighter
                children={deploymentYamlExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />

            <h3>kubectl cheat sheet</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Cluster &amp; context</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl cluster-info</code></td>
                        <td>Where the control plane is and what's running on it</td>
                    </tr>
                    <tr>
                        <td><code>kubectl config get-contexts</code></td>
                        <td>List every cluster/user combo in your kubeconfig</td>
                    </tr>
                    <tr>
                        <td><code>kubectl config use-context my-cluster</code></td>
                        <td>Switch which cluster subsequent commands target</td>
                    </tr>
                    <tr>
                        <td><code>kubectl config set-context --current --namespace=my-ns</code></td>
                        <td>Stop typing <code>-n my-ns</code> on every command</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Working with objects</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl get pods -o wide</code></td>
                        <td>List with node, IP and status — <code>-o wide</code> works on most resource
                            types
                        </td>
                    </tr>
                    <tr>
                        <td><code>kubectl describe pod my-pod</code></td>
                        <td>Full detail plus the event history — the first stop when something's wrong</td>
                    </tr>
                    <tr>
                        <td><code>kubectl apply -f deployment.yaml</code></td>
                        <td>Create or update to match the file — the normal way to change anything</td>
                    </tr>
                    <tr>
                        <td><code>kubectl delete -f deployment.yaml</code></td>
                        <td>Delete everything defined in that file</td>
                    </tr>
                    <tr>
                        <td><code>kubectl explain deployment.spec</code></td>
                        <td>Inline docs for any field, straight from the API schema</td>
                    </tr>
                    <tr>
                        <td><code>kubectl get all</code></td>
                        <td>Quick overview of Pods, Services, Deployments and ReplicaSets in the namespace</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Pods &amp; workloads</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl logs my-pod</code></td>
                        <td>Container logs; add <code>-c my-container</code> for a multi-container Pod</td>
                    </tr>
                    <tr>
                        <td><code>kubectl logs -f my-pod</code></td>
                        <td>Follow, like <code>tail -f</code></td>
                    </tr>
                    <tr>
                        <td><code>kubectl logs --previous my-pod</code></td>
                        <td>Logs from the container that just crashed, not the one that replaced it</td>
                    </tr>
                    <tr>
                        <td><code>kubectl exec -it my-pod -- /bin/sh</code></td>
                        <td>Get a shell inside a running container</td>
                    </tr>
                    <tr>
                        <td><code>kubectl port-forward my-pod 8080:80</code></td>
                        <td>Tunnel a local port straight to the Pod, bypassing any Service</td>
                    </tr>
                    <tr>
                        <td><code>kubectl top pod</code></td>
                        <td>Live CPU/memory usage (needs metrics-server installed)</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Deployments &amp; scaling</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl scale deployment/hello-web --replicas=5</code></td>
                        <td>Change the replica count directly</td>
                    </tr>
                    <tr>
                        <td><code>kubectl rollout status deployment/hello-web</code></td>
                        <td>Watch a rolling update until it finishes</td>
                    </tr>
                    <tr>
                        <td><code>kubectl rollout history deployment/hello-web</code></td>
                        <td>List previous revisions</td>
                    </tr>
                    <tr>
                        <td><code>kubectl rollout undo deployment/hello-web</code></td>
                        <td>Roll back to the previous revision</td>
                    </tr>
                    <tr>
                        <td><code>kubectl rollout restart deployment/hello-web</code></td>
                        <td>Restart every Pod, one at a time, without changing the image — picks up a
                            changed ConfigMap/Secret
                        </td>
                    </tr>
                    <tr>
                        <td><code>kubectl autoscale deployment/hello-web --min=2 --max=10 --cpu-percent=70</code>
                        </td>
                        <td>Create a HorizontalPodAutoscaler for it</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Debugging</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>kubectl get events --sort-by=.lastTimestamp</code></td>
                        <td>Cluster-wide events, oldest first — great for "what just happened"</td>
                    </tr>
                    <tr>
                        <td><code>kubectl get pods --field-selector=status.phase!=Running</code></td>
                        <td>Only the Pods that aren't healthy</td>
                    </tr>
                    <tr>
                        <td><code>kubectl describe node my-node</code></td>
                        <td>Node capacity, allocatable resources and why a Pod might not fit</td>
                    </tr>
                    <tr>
                        <td><code>kubectl debug my-pod -it --image=busybox</code></td>
                        <td>Attach an ephemeral debug container to a running Pod without restarting it</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Things that trip people up</h3>
            <ul>
                <li><b>CrashLoopBackOff</b> means the container keeps exiting right after it starts — check{' '}
                    <code>kubectl logs --previous</code>, not the live logs, which will just be empty.
                </li>
                <li><b>Pod stuck Pending</b> almost always means it can't be scheduled — insufficient CPU/memory
                    on any node, or a PVC that hasn't bound. <code>kubectl describe pod</code> spells out the
                    exact reason under Events.
                </li>
                <li><b>Deleting a Pod doesn't fix anything by itself</b> if a Deployment owns it — a
                    replacement is scheduled immediately and hits the same problem.
                </li>
                <li><b>A Service with no endpoints</b> almost always means its selector doesn't actually
                    match the Pod's labels — check both with <code>kubectl get endpoints</code> and{' '}
                    <code>kubectl get pods --show-labels</code>.
                </li>
                <li><b>Editing a ConfigMap doesn't restart anything</b> that consumed it as environment
                    variables — only a mounted file updates live, and even then the app has to notice. A{' '}
                    <code>rollout restart</code> is the reliable fix.
                </li>
                <li><b>Setting limits without requests</b> (or neither) leaves the scheduler guessing and
                    makes eviction under memory pressure unpredictable — always set both.
                </li>
            </ul>
        </div>
    )
}
